/** Page tabs: switch, add, rename (double-click), move and delete pages of a dashboard. */

import { useState, type ReactNode } from 'react';

import type { Page } from './reports-api.ts';

export function PageTabs({
  pages,
  active,
  editable,
  onSelect,
  onChange,
}: {
  pages: Page[];
  active: number;
  editable: boolean;
  onSelect: (index: number) => void;
  onChange: (pages: Page[], nextActive?: number) => void;
}): ReactNode {
  const [renaming, setRenaming] = useState<number | null>(null);
  const rename = (index: number, title: string) => {
    const clean = title.trim().slice(0, 120);
    if (clean) onChange(pages.map((page, at) => (at === index ? { ...page, title: clean } : page)));
    setRenaming(null);
  };
  const move = (index: number, by: number) => {
    const target = index + by;
    if (target < 0 || target >= pages.length) return;
    const next = [...pages];
    [next[index], next[target]] = [next[target] as Page, next[index] as Page];
    onChange(next, target);
  };
  return (
    <div className="rp" role="tablist" aria-label="Pages">
      {pages.map((page, index) =>
        renaming === index ? (
          <input
            key={page.id ?? index}
            className="rp-rename"
            aria-label="Page name"
            defaultValue={page.title}
            autoFocus
            onBlur={(event) => rename(index, event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') rename(index, event.currentTarget.value);
              if (event.key === 'Escape') setRenaming(null);
            }}
          />
        ) : (
          <button
            type="button"
            role="tab"
            key={page.id ?? index}
            aria-selected={index === active}
            className={`rp-tab${index === active ? ' rp-tab-on' : ''}`}
            onClick={() => onSelect(index)}
            onDoubleClick={() => editable && setRenaming(index)}
            title={editable ? 'Double-click to rename' : undefined}
          >
            {page.title}
          </button>
        ),
      )}
      {editable && (
        <span className="rp-tools">
          <button
            type="button"
            className="rb-icon"
            aria-label="Move page left"
            disabled={active === 0}
            onClick={() => move(active, -1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="rb-icon"
            aria-label="Move page right"
            disabled={active === pages.length - 1}
            onClick={() => move(active, 1)}
          >
            ›
          </button>
          <button
            type="button"
            className="rb-icon"
            aria-label="Delete this page"
            disabled={pages.length < 2}
            onClick={() =>
              onChange(
                pages.filter((_, at) => at !== active),
                Math.max(0, active - 1),
              )
            }
          >
            🗑
          </button>
          <button
            type="button"
            className="linklike"
            disabled={pages.length >= 30}
            onClick={() =>
              onChange(
                [...pages, { title: `Page ${pages.length + 1}`, settings: {}, widgets: [] }],
                pages.length,
              )
            }
          >
            + Add page
          </button>
        </span>
      )}
    </div>
  );
}
